import type { FacilityKind, GoodType } from "./types";

// ---------------------------------------------------------------------------
// session
// ---------------------------------------------------------------------------

/**
 * The simulation clock. Every rule is denominated in ticks so production,
 * pressure, convoys and order lifetimes all advance on the same deterministic
 * timeline. Territorial unification can end a run early; otherwise the same
 * deterministic clock also owns the ranked duration below.
 */
export const TICK_RATE_HZ = 5;

export const secondsToTicks = (seconds: number): number => Math.round(seconds * TICK_RATE_HZ);

/**
 * Seven simulated minutes: one symbolic minute for each of the seven nations.
 *
 * This was first shipped as seven wall-clock minutes. Playtesting showed that
 * the countdown no longer described the board when pause/x2/x4 changed the war
 * underneath it. At 5 Hz this is exactly 2,100 ticks, so pause freezes the
 * ranked window and x2/x4 intentionally finish it in 3:30/1:45 of wall time.
 */
export const ROUND_DURATION_SECONDS = 7 * 60;
export const ROUND_DURATION_TICKS = secondsToTicks(ROUND_DURATION_SECONDS);
export const SIMULATION_TIME_LIMIT_REASON = "simulation time limit reached";

export const GOOD_TYPES: readonly GoodType[] = ["weapons", "resources"] as const;
export const FACILITY_KINDS: readonly FacilityKind[] = ["resource", "weapon"] as const;

export const GOOD_LABEL: Record<GoodType, string> = {
  weapons: "WEAPONS",
  resources: "RESOURCES",
};

// ---------------------------------------------------------------------------
// combat
//
// Weapons are the army. There is no unit triangle: strength is what stands in
// the province, modified by fortification, supply and morale.
// ---------------------------------------------------------------------------

export const ATTACK_WEIGHT = 1.0;
/**
 * Every defender receives the requested 20% home-ground advantage before its
 * fortification, supply and morale are applied. This replaces the measured 1.05
 * baseline; capture progress now has its own explicit 2:1 troop gate below, so
 * the defensive bonus no longer has to carry both meanings.
 */
export const DEFENSE_WEIGHT = 1.2;

/**
 * A province with no adjacent friendly retreat gains another 20 percentage
 * points: 1.20 + 0.20 = 1.40, exactly the requested total 40% defence bonus.
 * Such a garrison ignores capture pressure and must be destroyed to the last
 * whole weapon before ownership can change.
 */
export const NO_RETREAT_DEFENSE_BONUS = 0.2;

export const COMMITMENT_FACTOR = {
  LOW: 0.55,
  MEDIUM: 0.8,
  HIGH: 1.0,
} as const;

/** Fraction of a province's weapons that stays behind rather than assaulting. */
export const GARRISON_HOLDBACK = {
  LOW: 0.55,
  MEDIUM: 0.3,
  HIGH: 0.12,
} as const;

/**
 * Multiplier for the combat clock only; production, trade and shipping stay on
 * the base simulation clock. In the fixed overwhelming HANGU -> HEXI fixture,
 * 1x took 112 ticks (22.4 seconds) including the opening capture cooldown;
 * 2x takes 55 ticks (11 seconds). Casualties scale with pressure so the same
 * battle consumes approximately the same stock instead of becoming cheaper.
 */
export const COMBAT_TIME_SCALE = 2;

/** Pressure gained per tick equals (ratio - 1) * this, clamped below. */
export const PRESSURE_GAIN_PER_TICK = 0.8 * COMBAT_TIME_SCALE;
export const PRESSURE_MAX_GAIN_PER_TICK = 1.2 * COMBAT_TIME_SCALE;
export const PRESSURE_MAX_LOSS_PER_TICK = 0.5 * COMBAT_TIME_SCALE;
/** Pressure bled off per tick on a front with no active attack order. */
export const PRESSURE_IDLE_DECAY = 0.35 * COMBAT_TIME_SCALE;
export const PRESSURE_CAPTURE_THRESHOLD = 100;
/**
 * Capture pressure may rise only when committed attacking weapons are at least
 * twice the defending weapons. The prior 1:1 effective-power gate let the
 * central 104-vs-66 opening force accumulate occupation progress while most of
 * the garrison remained. Two-to-one is now a hard troop-count prerequisite;
 * fortification and the 20% defence bonus still determine the rate above it.
 */
export const CAPTURE_FORCE_RATIO = 2;

/**
 * Weapons destroyed per tick, as a fraction of the engagement — which is the
 * weaker of the two sides, so a battle is only as bloody as the smaller army
 * present.
 */
export const CASUALTY_RATE = 0.0009 * COMBAT_TIME_SCALE;
/** Storming a position costs more than holding one. */
export const ATTACKER_CASUALTY_BIAS = 1.25;
/**
 * Weapons are displayed and reasoned about as whole soldiers. Without a whole-
 * unit defeat threshold, proportional casualties leave an encircled garrison
 * at a positive fraction forever: 10 defenders under overwhelming attack still
 * survive past the 2,100-tick ranked boundary. Dropping the final sub-unit to
 * zero lets the requested last stand resolve while never skipping a whole
 * surviving weapon.
 */
export const MIN_EFFECTIVE_WEAPONS = 1;

export const SUPPLY_DISCONNECTED = 0.55;
export const SUPPLY_RECALC_INTERVAL_TICKS = 20;

export const MORALE_MIN = 0.45;
export const MORALE_MAX = 1.25;
export const MORALE_CAPTURE_GAIN = 0.06;
export const MORALE_LOSS_ON_PROVINCE_LOST = 0.1;
export const MORALE_RECOVERY_PER_TICK = 0.00025;

/** A province cannot be assaulted again for this many ticks after flipping. */
export const CAPTURE_COOLDOWN_TICKS = 30 / COMBAT_TIME_SCALE;

/** Share of the assaulting province's weapons that occupies the new ground. */
export const OCCUPATION_SHARE = 0.45;
/** Fortification a province keeps after changing hands. */
export const CAPTURED_FORTIFICATION_RETAINED = 0.4;
/** Facilities are dug in: this share survives a change of ownership. */
export const CAPTURED_FACILITY_SHARE = 0.6;

// ---------------------------------------------------------------------------
// economy
//
// Resources are mined, then spent to build weapons. A nation that wants a
// bigger army has to widen the pipe first, which is what makes both goods worth
// trading and why a war raises demand for both.
// ---------------------------------------------------------------------------

/**
 * Shared scale for every recurring nation-side cash and production output.
 *
 * The requested 1.2x boost is applied as one factor rather than five unrelated
 * edits, preserving the economy's ratios. The measured baseline and boosted
 * outputs per cycle/tax tick are:
 *
 *   mine ore       3.5 -> 4.2
 *   factory arms   1.4 -> 1.68
 *   province tax   520 -> 624
 *   mine tax       180 -> 216
 *   house tax      620 -> 744
 *
 * Starting treasuries, market prices and broker cash are intentionally outside
 * this scale: it raises what nations earn and produce during play, not what a
 * run starts with or what a trade is worth.
 */
export const NATION_ECONOMY_MULTIPLIER = 1.2;

/**
 * User-requested 20% increase to opening ore only (2026-08-28).
 * The 28 province definitions total 1,536 ore; applying 1.2 once at world
 * creation yields 1,843.2. A 40-ore province opens with 48 and a 190-ore depot
 * with 228. Preserve local stock ratios and fractional ore without rounding;
 * weapons, cash, facilities and recurring production stay outside this scale.
 */
export const STARTING_RESOURCE_MULTIPLIER = 1.2;

/**
 * User-requested +2 to each facility kind's opening minimum and maximum.
 * Across 28 provinces, houses rise from 7 (0-1 each) to 63 (2-3), mines from
 * 38 (0-3) to 94 (2-5), and factories from 33 (0-4) to 89 (2-6).
 * Apply once at world creation: all 168 extra facilities preserve the original
 * differences between provinces. Per-facility yields and costs are unchanged.
 */
export const STARTING_FACILITY_BONUS_PER_KIND = 2;

export const PRODUCTION_INTERVAL_TICKS = 40;
/** Upkeep has its own ten-game-second clock; mining and assembly remain at eight. */
export const UPKEEP_INTERVAL_TICKS = secondsToTicks(10);

/**
 * Resources one mine yields per production cycle.
 *
 * Set by a rule, not by a target outcome: ONE MINE FEEDS ONE FACTORY. A factory
 * eats `WEAPONS_PER_FACTORY * RESOURCES_PER_WEAPON`, so the mine and factory
 * outputs are meant to move together.
 *
 * At 2.2 a factory took 1.6 mines, which made the build order answer itself:
 * about 61% of everything anyone raised had to be a mine before a single weapon
 * could be armed. Over a full round the map spent itself digging — 74 of the
 * ~105 facilities standing at the end were mines, the continent finished with
 * 892 ore on it across 28 provinces, and there was so little ore anywhere that
 * the broker's ore trade had nothing to move. At 1:1 the same round ends with
 * 1821 ore standing and the factory count growing rather than shrinking.
 *
 * Measured, whole rounds on the demo seed, mine yield against what it left:
 *
 *   2.2  ore 892   army 1104  mines 74 factories 31   6/7 nations alive
 *   3.5  ore 1821  army 1332  mines 71 factories 37   7/7
 *   4.4  ore 2326  army 1610  mines 60 factories 37   6/7
 *   5.5  ore 3236  army 1722  mines 58 factories 44   7/7
 *
 * Starvation does not fall as this rises — it holds near 30% of everything ever
 * armed at every value, because a richer board simply arms more. That is the
 * design working: a weapon is a standing bill, and some of them go unpaid. What
 * changes is whether there is ore on the board to trade, and past the measured
 * 3.5 baseline it becomes a glut nobody needs to buy.
 *
 * Those rounds selected the 3.5 / 1.4 baseline. The shared 1.2x economy boost
 * raises both sides to 4.2 / 1.68, so one mine still feeds one factory exactly.
 */
export const RESOURCES_PER_FACILITY = 3.5 * NATION_ECONOMY_MULTIPLIER;
/** Weapons one factory can assemble per production cycle, if fed; shares the 1.2x scale above. */
export const WEAPONS_PER_FACTORY = 1.4 * NATION_ECONOMY_MULTIPLIER;
/** Resources consumed per weapon assembled. */
export const RESOURCES_PER_WEAPON = 2.5;
/**
 * Ten-second upkeep bills held back before a factory may convert anything.
 *
 * A weapon is not a thing you own, it is a thing you feed: every one assembled
 * adds 0.08 ore in peace or 0.13 in combat every ten seconds. A full line would eat the
 * food of the soldiers standing on the same ground and they would starve a cycle
 * later — measured when factories drew nationally, 26 of 28 provinces at zero ore
 * with every nation losing weapons. The army already in the field is the first
 * call on the ore, and the line only gets what is over that.
 *
 * Both halves are now local: the reserve a factory must leave behind is its own
 * province's garrison bill, not the nation's. One national bill idled every
 * factory in the country at once — measured over a round, 15 of 35 factories
 * standing idle against 3 of 37 once the reserve followed the ore.
 */
export const UPKEEP_RESERVE_CYCLES = 6;

/**
 * The opening +2 houses/mines/factories adds six buildings to every province.
 * Raise the shared cap from 6 to 12 so the original 0-5 opening totals become
 * 6-11 without losing any of the original 1-6 remaining construction slots.
 */
export const MAX_FACILITIES_PER_PROVINCE = 6 + 3 * STARTING_FACILITY_BONUS_PER_KIND;
/**
 * One construction start per nation per simulated minute.
 *
 * Commanders already review no faster than every 20 seconds, so a requested
 * 10-second gate changed nothing. Full demo-seed rounds measured:
 *
 *   cooldown   builds   construction spend   final combined treasury
 *      none       56          11.34m                  2.13m
 *       10s       56          11.34m                  2.13m
 *       60s       41           8.28m                  4.80m
 *       90s       33           6.62m                  6.29m
 *
 * Sixty seconds removes roughly thirty percent of construction spending while
 * preserving substantially more industrial growth than the 90-second gate.
 * The clock is nation-wide: building in any province starts it for that nation.
 */
export const FACILITY_BUILD_COOLDOWN_TICKS = secondsToTicks(60);
/**
 * Deliberately steep. At half these prices a nation tripled its industry inside
 * the original 1,800-tick balance sample, which swamped the starting positions
 * the whole scenario is built on.
 */
export const FACILITY_COST: Record<FacilityKind, number> = {
  resource: 190_000,
  weapon: 260_000,
  // Far under a mine because it yields nothing but money. In the original
  // 1,800-tick measurement window tax arrived 180 times: at this price a house
  // cleared its cost in about 145 seconds at the measured 620 baseline, or 121
  // seconds after the shared 1.2x income boost, and roughly doubled by tick
  // 1,800. Priced any higher and no nation could justify one in the measured opening.
  housing: 45_000,
};

export const TAX_INTERVAL_TICKS = 10;
export const TAX_PER_PROVINCE = 520 * NATION_ECONOMY_MULTIPLIER;
/** Extra income per resource facility: a mine pays tax as well as ore. */
export const TAX_PER_RESOURCE_FACILITY = 180 * NATION_ECONOMY_MULTIPLIER;
/**
 * A house is worth about one more province's worth of tax. It is the only
 * building a nation can buy that buys the next building. It shares the same
 * 1.2x national income scale as land and mine tax.
 */
export const TAX_PER_HOUSE = 620 * NATION_ECONOMY_MULTIPLIER;

export const FORTIFY_COST_PER_POINT = 24_000;
export const FORTIFY_MAX = 0.75;

// ---------------------------------------------------------------------------
// movement / logistics
// ---------------------------------------------------------------------------

/** Ticks to traverse one province link. */
export const MOVEMENT_TICKS_PER_LINK = 34;
export const MOVEMENT_SPEED = 1 / MOVEMENT_TICKS_PER_LINK;
export const MAX_ACTIVE_MOVEMENTS_PER_NATION = 8;
/** Non-capital provinces leave this share of their weapons as a local garrison. */
export const NON_CAPITAL_GARRISON_RESERVE_RATIO = 0.1;
/** A nation can only press so many assaults at once. */
export const MAX_ATTACK_ORDERS_PER_NATION = 3;
/** Upper bound on any single agent-issued deployment. */
export const MAX_DEPLOY_QUANTITY = 80;

/**
 * Map units a convoy covers per tick. Tuned so a broker delivery lands in about
 * four seconds — buy, sell, arrival and the front changing all have to fit
 * inside the first ten seconds of a demo.
 */
export const SHIPMENT_SPEED_PER_TICK = 14;

// ---------------------------------------------------------------------------
// market
// ---------------------------------------------------------------------------

export const GOOD_BASE_PRICE: Record<GoodType, number> = {
  weapons: 24_000,
  resources: 7_000,
};

export const PRICE_FLOOR_FACTOR = 0.4;
export const PRICE_CEILING_FACTOR = 3.5;

/**
 * A nation buying pays a war premium; a nation liquidating takes a discount.
 * Together they are the whole of the broker's margin on a round trip.
 *
 * This has now been set three times and the history is the argument.
 *
 *   1.55 / 0.78   spread 0.77, round trip 1.99x   too wide
 *   1.30 / 0.88   spread 0.42, round trip 1.48x   too narrow
 *   1.45 / 0.82   spread 0.63, round trip 1.77x
 *
 * 1.55/0.78 was narrowed because a near-2x round trip paid the same whether or
 * not the run was well judged — buy from anyone at peace, sell to anyone at war,
 * and WHO barely mattered. 1.30/0.88 overshot the other way: measured across a
 * full round the margin was a flat 44% of the weapons index at the opening and
 * 27% by the midgame, which is thin enough that a good read is not worth much
 * more than a poor one either.
 *
 * 1.45/0.82 is the spread widened by half again, which is what the boss asked
 * for on measuring it. Each side keeps its own meaning — the premium is what a
 * war pays over fair, the discount what a forced seller gives up — so the
 * deviation of each from 1.0 is scaled, not the pair recentred.
 *
 * Measured at ticks 0, 1500 and 3000, the margin on both goods comes out at
 * exactly 1.500x its old value, so the widening reaches the board rather than
 * being eaten by the clamps. The one thing it does move: by the midgame a single
 * resource bid in seven now reaches the 3.5x ceiling, where none did before.
 * That is the clamp holding the most desperate state on the board, which is what
 * it is for, and the median bid is nowhere near it.
 */
export const PRICE_WAR_PREMIUM = 1.45;
export const PRICE_LIQUIDATION_DISCOUNT = 0.82;

/**
 * How much of a nation's own price deviation survives.
 *
 * The side bias above is only part of the gap. The rest is the nation itself —
 * scarcity, war demand, treasury — and multiplied together those swing a price
 * far harder: measured on the opening board, buying weapons from 제 and selling
 * them to 위 was a **4.1x** round trip, of which only 1.48x was the side bias.
 * At that margin the trade barely has to be a good one.
 *
 * Compressing the deviation rather than the factors keeps the ORDER intact —
 * the desperate buyer still pays the most and the glutted seller still asks the
 * least, so finding the right pair is still the game — it just no longer pays
 * four to one for finding it. Measured after: 2.5x.
 */
export const PRICE_DEVIATION_KEPT = 0.55;

/**
 * Market impact: the player's own trades move the price against them.
 *
 * Buying takes stock off the board and the reference price rises; selling adds
 * to it and the price falls. Without this the broker was a price-taker in a
 * market it was the largest participant in — it could work the same spread all
 * round at the same numbers, and the only limit on a good trade was how fast the
 * player could click.
 *
 * Per unit traded, accumulated on `world.brokerPressure` and folded into the
 * next sample.
 */
export const BROKER_PRICE_IMPACT = 0.004;
/** What is left of that lean after one sampling interval — about a minute to fade. */
export const BROKER_PRESSURE_DECAY = 0.86;
/** Ceiling either way, so a single enormous run cannot break the price band. */
export const BROKER_PRESSURE_CAP = 60;
/** How much of the final order price the agent's own asking price accounts for. */
export const AGENT_PRICE_WEIGHT = 0.4;

/**
 * In ticks, not seconds. Everything else here is tick-denominated, and pinning
 * this one to the clock would have quietly halved how much happens inside an
 * order's life when the tick rate halved — the one balance change the slow-down
 * was supposed not to make. 500 ticks is 100 s at 5 Hz.
 */
export const ORDER_TTL_TICKS = 500;
export const MAX_ORDER_QUANTITY = 60;
/** Per nation, per side. A nation can want weapons and sell resources at once. */
export const MAX_OPEN_ORDERS_PER_NATION = 4;
export const PRICE_SAMPLE_INTERVAL_TICKS = 20;

// ---------------------------------------------------------------------------
// broker
// ---------------------------------------------------------------------------

/**
 * Base ore per weapon per ten-game-second upkeep interval. This is linear:
 * no extra multiplier is applied merely because a garrison is larger.
 *
 * Paid out of the province the garrison is standing in, and out of nothing else.
 * That locality is the whole mechanic, and it now governs the factories too:
 * everything on this board is spent where it stands, so holding a border
 * province with no mine under it costs a supply line rather than nothing.
 * Before this, weapons were free to park and they piled up in the capital —
 * measured on the demo seed, 진 finished a round with 542 weapons and only 109
 * of them anywhere near a front.
 *
 * User-requested combat pricing (2026-08-29): base 0.1 per ten seconds,
 * +30% on ground actually fighting, -20% on all other ground. Measured with
 * 100 fixed weapons, no mining/assembly/casualties, over 40 game seconds:
 *
 *   former 0.08 / 8 s      40 ore in either state
 *   new peace 0.08 / 10 s  32 ore (-20%)
 *   new combat 0.13 / 10 s 52 ore (+30%)
 *
 * Mining/assembly retain their 40-tick clock and ore locality. A missing unit
 * of upkeep still costs one weapon. Active combat is per province, not per
 * country or border; multiple assaults do not multiply this tariff again.
 */
export const WEAPON_UPKEEP_PER_CYCLE = 0.1;
/** Requested multipliers; the fixed-army measurement above covers both. */
export const COMBAT_UPKEEP_MULTIPLIER = 1.3;
export const PEACE_UPKEEP_MULTIPLIER = 0.8;

export const BROKER_START_CASH = 250_000;

// ---------------------------------------------------------------------------
//
// There is deliberately no broker-facing reputation here.
//
// The design has exactly four factors, and all four are between nations: each
// nation's military power, and the deciding nation's trust and regard toward
// the others. A fifth axis measuring how the world felt about the *player* was
// never part of it — it priced every trade, penalised the final score, and gave
// the player a meter to manage instead of a war to read. Removed rather than
// tuned: `trustToward` / `regardToward` are the only standings that exist.
//
// ---------------------------------------------------------------------------
// credit
//
// A nation that cannot pay cash can still be armed, on the broker's book. That
// is how a losing side is kept alive — and how the broker is paid more for it.
// The risk is real: a nation that stops existing never settles.
// ---------------------------------------------------------------------------

/** Interest compounds on the outstanding balance this often. */
export const DEBT_INTEREST_INTERVAL_TICKS = 200;
export const DEBT_INTEREST_RATE = 0.02;

/** Share of a nation's tax income that goes to servicing its debt. */
export const DEBT_REPAYMENT_SHARE = 0.45;

/**
 * How much a nation can owe. Territory is the collateral, so a nation losing the
 * war can borrow less, not more — the broker's exposure shrinks as its client does.
 *
 * Tripled from 120K/90K. At the old line a two-province state could borrow $300K,
 * which bought it roughly one convoy — enough to notice the mechanic, not enough
 * to change who was winning. The point of credit is that the broker can decide a
 * war it has no army in, and that needs a line big enough to actually turn one.
 */
export const DEBT_LIMIT_BASE = 360_000;
export const DEBT_LIMIT_PER_PROVINCE = 270_000;
